import React, { useState } from "react";
import styles from "./Assessment.module.css";

const ResultsDock = ({
  activeTab,
  onTabChange,
  runResult,
  submissions,
  totalCases,
  onRestoreSubmission,
  onRunSingleCase,
}) => {
  const [expandedCase, setExpandedCase] = useState(null);
  const [viewingStderr, setViewingStderr] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const handleCopyInput = (input) => {
    navigator.clipboard.writeText(input || "").then(() => {
      console.log("Input copied to clipboard");
    });
  };

  const handleRunSingleCase = (testCase) => {
    if (onRunSingleCase) {
      onRunSingleCase(testCase.input);
    }
  };

  const handleViewStderr = (errorMessage) => {
    setViewingStderr(errorMessage);
  };

  const handleViewSubmissionResults = (submission) => {
    if (submission.latest_run?.test_case_results) {
      setSelectedSubmission(submission);
      onTabChange("testcases");
    }
  };

  // Use selected submission's results if viewing a past submission, otherwise use current run result
  const displayResult = selectedSubmission ? selectedSubmission.latest_run : runResult;
  const testCaseResults = displayResult?.test_case_results || [];
  const passedCases = testCaseResults.filter(tc => tc.is_correct).length;
  const totalTestCases = testCaseResults.length || totalCases;
  const isCustomInput = displayResult?.is_custom_input;

  // Clear selected submission when switching to non-testcases tab or when new run completes
  const handleTabChange = (tab) => {
    if (tab !== "testcases") {
      setSelectedSubmission(null);
    }
    onTabChange(tab);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };

  const getStateInfo = (submission) => {
    const latestRun = submission.latest_run;
    if (!latestRun) return { label: "Draft", class: "stateError", dot: "dotWarning" };

    if (latestRun.state === "error") {
      return { label: "Error", class: "stateError", dot: "dotWarning" };
    }

    const passed = latestRun.passed_cases || 0;
    const total = latestRun.total_cases || 0;

    if (passed === total && total > 0) {
      return { label: `Passed ${passed}/${total}`, class: "statePassed", dot: "dotSuccess" };
    }

    if (passed === 0) {
      return { label: "Failed", class: "stateFailed", dot: "dotError" };
    }

    return { label: `Passed ${passed}/${total}`, class: "statePassed", dot: "dotSuccess" };
  };

  return (
    <div className={styles.resultsDock}>
      {/* Tabs */}
      <div className={styles.dockTabs}>
        <button
          className={`${styles.dockTab} ${activeTab === "testcases" ? styles.dockTabActive : ""}`}
          onClick={() => { setSelectedSubmission(null); handleTabChange("testcases"); }}
        >
          Test cases
          <span className={`${styles.tabPill} ${activeTab === "testcases" ? styles.tabPillActive : ""}`}>
            {testCaseResults.length || totalCases}
          </span>
        </button>
        <button
          className={`${styles.dockTab} ${activeTab === "output" ? styles.dockTabActive : ""}`}
          onClick={() => handleTabChange("output")}
        >
          Output
          <span className={styles.tabPill}>—</span>
        </button>
        <button
          className={`${styles.dockTab} ${activeTab === "submissions" ? styles.dockTabActive : ""}`}
          onClick={() => handleTabChange("submissions")}
        >
          Submissions
          <span className={styles.tabPill}>{submissions.length}</span>
        </button>
      </div>

      {/* Results Banner (when run is complete or viewing past submission) */}
      {displayResult && displayResult.state === "done" && (
        <div className={`${styles.resultsBanner} ${isCustomInput ? styles.infoBanner : (passedCases === totalTestCases ? styles.successBanner : styles.errorBanner)}`}>
          <div className={`${styles.resultIcon} ${isCustomInput ? styles.infoIcon : (passedCases === totalTestCases ? styles.successIcon : styles.errorIcon)}`}>
            {isCustomInput ? "▶" : (passedCases === totalTestCases ? "✓" : "✕")}
          </div>
          <div className={styles.resultInfo}>
            <div className={`${styles.resultTitle} ${isCustomInput ? styles.infoTitle : (passedCases === totalTestCases ? styles.successTitle : styles.errorTitle)}`}>
              {isCustomInput ? "Custom input executed" : `${passedCases} of ${totalTestCases} test cases passed`}
              {selectedSubmission && (
                <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 8, color: "#7b93a4" }}>
                  (Submission #{selectedSubmission.id})
                </span>
              )}
            </div>
            <div className={styles.resultMeta}>
              {isCustomInput
                ? `Completed in ${displayResult.total_runtime?.toFixed(1) || "—"}s`
                : `Score ${displayResult.score} / ${displayResult.max_score} · completed in ${displayResult.total_runtime?.toFixed(1) || "—"}s`
              }
              {selectedSubmission && (
                <button
                  onClick={() => setSelectedSubmission(null)}
                  style={{
                    marginLeft: 12,
                    background: "none",
                    border: "1px solid currentColor",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 11,
                    cursor: "pointer",
                    color: "inherit",
                  }}
                >
                  Back to current
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={styles.dockContent}>
        {/* Test Cases Tab */}
        {activeTab === "testcases" && (
          <>
            {testCaseResults.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>▶</div>
                <div className={styles.emptyTitle}>No run yet</div>
                <div className={styles.emptyText}>
                  Run your code to see each test case, its output and runtime here. Results stay docked — nothing covers your editor.
                </div>
              </div>
            ) : (
              <>
                {/* Case Chips */}
                <div className={styles.caseChips}>
                  {testCaseResults.map((tc, i) => (
                    <div
                      key={tc.id || i}
                      className={`${styles.caseChip} ${
                        tc.is_hidden ? styles.chipHidden :
                        tc.is_correct ? styles.chipPassed : styles.chipFailed
                      }`}
                      onClick={() => setExpandedCase(expandedCase === i ? null : i)}
                    >
                      {i + 1}
                    </div>
                  ))}
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginLeft: 12, fontSize: 12.5, color: "#7b93a4" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: "#1f9d4d" }} />
                      Passed
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: "#d94f4f" }} />
                      Failed
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: "#c4cdd6" }} />
                      Hidden
                    </span>
                  </div>
                </div>

                {/* Test Case Details */}
                {testCaseResults.map((tc, i) => {
                  const isExpanded = expandedCase === i;
                  const isFailed = !tc.is_correct && !tc.is_hidden;

                  return (
                    <div key={tc.id || i} className={styles.caseDetail}>
                      <div
                        className={`${styles.caseDetailHeader} ${isExpanded ? styles.caseDetailExpanded : styles.caseDetailCollapsed}`}
                        onClick={() => setExpandedCase(isExpanded ? null : i)}
                      >
                        <span className={styles.caseMark} style={{ color: tc.is_correct ? "#1f9d4d" : "#c93c3c" }}>
                          {tc.is_correct ? "✓" : "✕"}
                        </span>
                        <span className={styles.caseLabel}>
                          Test case {i + 1}
                          {tc.is_hidden && " · hidden"}
                        </span>
                        <span className={styles.caseTime}>{tc.run_time || "—"}s</span>
                        <span className={`${styles.caseScore} ${tc.is_correct ? styles.scorePass : styles.scoreFail}`}>
                          Score {tc.score || 0} / {tc.score || 5}
                        </span>
                        <span style={{ color: "#b9c7d2", fontSize: 11 }}>
                          {isExpanded ? "▲" : "▼"}
                        </span>
                      </div>

                      {isExpanded && !tc.is_hidden && (
                        <>
                          <div className={styles.caseDetailBody}>
                            <div className={styles.diffColumn}>
                              <div className={styles.diffLabel}>Input</div>
                              <div className={styles.diffValue}>{tc.input || "(empty)"}</div>
                            </div>
                            <div className={styles.diffColumn}>
                              <div className={styles.diffLabel} style={{ color: "#1f8a4c" }}>Expected</div>
                              <div className={`${styles.diffValue} ${styles.diffExpected}`}>
                                {tc.expected_output || "(empty)"}
                              </div>
                            </div>
                            <div className={styles.diffColumn}>
                              <div className={styles.diffLabel} style={{ color: isFailed ? "#c93c3c" : "#1f8a4c" }}>
                                Your output
                              </div>
                              <div className={`${styles.diffValue} ${isFailed ? styles.diffActual : styles.diffExpected}`}>
                                {tc.output || "(empty)"}
                              </div>
                            </div>
                          </div>
                          {tc.error_message && (
                            <div style={{
                              padding: "12px 18px",
                              background: "#fdf3f3",
                              borderTop: "1px solid #f0d5d5",
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 12,
                              color: "#b03636",
                              whiteSpace: "pre-wrap",
                            }}>
                              {tc.error_message}
                            </div>
                          )}
                          <div className={styles.caseActions}>
                            <button
                              className={styles.caseActionBtn}
                              onClick={() => handleCopyInput(tc.input)}
                            >
                              Copy input
                            </button>
                            <button
                              className={styles.caseActionBtn}
                              onClick={() => handleRunSingleCase(tc)}
                            >
                              Run just this case
                            </button>
                            {tc.error_message && (
                              <button
                                className={styles.caseActionBtn}
                                onClick={() => handleViewStderr(tc.error_message)}
                              >
                                View stderr
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* Output Tab */}
        {activeTab === "output" && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📝</div>
            <div className={styles.emptyTitle}>Console output</div>
            <div className={styles.emptyText}>
              Output from your program will appear here when you run your code.
            </div>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === "submissions" && (
          <div className={styles.submissionsPanel}>
            {submissions.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📜</div>
                <div className={styles.emptyTitle}>No submissions yet</div>
                <div className={styles.emptyText}>
                  Your submission history will appear here after you run your code.
                </div>
              </div>
            ) : (
              <>
                {submissions.map((submission, i) => {
                  const stateInfo = getStateInfo(submission);
                  const latestRun = submission.latest_run;

                  const hasResults = latestRun?.test_case_results?.length > 0;
                  return (
                    <div
                      key={submission.id}
                      className={`${styles.submissionRow} ${i === 0 ? styles.submissionCurrent : ""} ${hasResults ? styles.submissionClickable : ""}`}
                      onClick={() => hasResults && handleViewSubmissionResults(submission)}
                      style={{ cursor: hasResults ? "pointer" : "default" }}
                    >
                      <div className={`${styles.submissionDot} ${styles[stateInfo.dot]}`} />
                      <div className={styles.submissionInfo}>
                        <div className={styles.submissionIdRow}>
                          <span className={styles.submissionId}>#{submission.id}</span>
                          <span className={`${styles.submissionState} ${styles[stateInfo.class]}`}>
                            {stateInfo.label}
                          </span>
                          {hasResults && (
                            <span style={{ fontSize: 11, color: "#7b93a4", marginLeft: 6 }}>
                              (click to view)
                            </span>
                          )}
                        </div>
                        <div className={styles.submissionMeta}>
                          {formatTime(submission.timestamp)} · {submission.language}
                          {latestRun?.total_runtime && ` · ${latestRun.total_runtime.toFixed(1)}s`}
                        </div>
                      </div>
                      <div className={styles.submissionScore}>
                        <span
                          className={styles.scoreValue}
                          style={{ color: latestRun?.score > 0 ? "#1f7a45" : "#4a6070" }}
                        >
                          {latestRun?.score || 0}
                        </span>
                        <span className={styles.scoreCases}>
                          {latestRun ? `${latestRun.passed_cases || 0} / ${latestRun.total_cases || 0} cases` : "—"}
                        </span>
                      </div>
                      <button
                        className={styles.restoreBtn}
                        onClick={(e) => { e.stopPropagation(); onRestoreSubmission(submission); }}
                      >
                        {i === 0 ? "Current" : "Restore"}
                      </button>
                    </div>
                  );
                })}

                {/* Score Progression Chart */}
                {submissions.length > 1 && (
                  <div className={styles.progressionChart}>
                    <div className={styles.sectionLabel}>Score progression</div>
                    <div className={styles.chartBars}>
                      {submissions.slice(0, 5).reverse().map((sub, i) => {
                        const score = sub.latest_run?.score || 0;
                        const maxScore = sub.latest_run?.max_score || 40;
                        const height = maxScore > 0 ? (score / maxScore) * 50 : 4;
                        const isError = !sub.latest_run || sub.latest_run.state === "error";

                        return (
                          <div key={sub.id} className={styles.chartBar}>
                            <div
                              className={styles.barFill}
                              style={{
                                height: Math.max(4, height),
                                background: isError ? "#f0c5c5" : score > 0 ? "#1f9d4d" : "#c9dcea",
                              }}
                            />
                            <span className={styles.barLabel}>#{sub.id}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Last Attempt Info (when no results) */}
      {!runResult && submissions.length === 0 && (
        <div style={{
          borderTop: "1px solid #eef2f6",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 9,
        }}>
          <div className={styles.sectionLabel}>Last attempt</div>
          <div style={{ fontSize: 13.5, color: "#7b93a4" }}>
            No submissions for this question yet.
          </div>
        </div>
      )}

      {/* Stderr Modal */}
      {viewingStderr && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setViewingStderr(null)}
        >
          <div
            style={{
              background: "#1a1a2e",
              borderRadius: 12,
              padding: 24,
              maxWidth: 600,
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}>
              <h3 style={{ color: "#fff", margin: 0, fontSize: 16 }}>Error Output (stderr)</h3>
              <button
                onClick={() => setViewingStderr(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#888",
                  fontSize: 20,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
            <pre style={{
              background: "#0f0f1a",
              padding: 16,
              borderRadius: 8,
              color: "#ff6b6b",
              fontSize: 13,
              fontFamily: "'JetBrains Mono', monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: 0,
            }}>
              {viewingStderr}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsDock;
